import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { renderIcon, DefaultCampaignIcon } from '../../utils/renderIcon';
import './styles.css';

/**
 * CampaignPickerModal Component
 * 
 * A modal that lets users pick which campaign to add a new mission to
 * when in multi-campaign (saved view) mode.
 * 
 * @param {string} mode - 'add' for adding new mission, 'paste' for pasting from clipboard
 */
const CampaignPickerModal = ({
  isOpen,
  campaigns = [],
  onSelect,
  onCancel,
  mode = 'add', // 'add' or 'paste'
}) => {
  // Mode-specific text
  const isPasteMode = mode === 'paste';
  const title = isPasteMode ? 'Paste Missions' : 'Add Mission';
  const message = isPasteMode 
    ? 'Which campaign should the pasted missions be added to?'
    : 'Which campaign should this mission be added to?';
  const confirmText = isPasteMode ? 'Paste' : 'Add Mission';
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const confirmBtnRef = useRef(null);

  // Auto-select first campaign and focus confirm button when modal opens
  useEffect(() => {
    if (isOpen) {
      if (campaigns.length > 0 && !selectedCampaignId) {
        setSelectedCampaignId(campaigns[0].id);
      }
      // Slight delay to ensure DOM is ready
      setTimeout(() => confirmBtnRef.current?.focus(), 50);
    } else {
      setSelectedCampaignId(null);
    }
  }, [isOpen, campaigns, selectedCampaignId]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel?.();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onCancel?.();
    } else if (e.key === 'Enter' && selectedCampaignId) {
      const campaign = campaigns.find(c => c.id === selectedCampaignId);
      if (campaign) onSelect?.(campaign);
    }
  };

  const handleConfirm = () => {
    const campaign = campaigns.find(c => c.id === selectedCampaignId);
    if (campaign) onSelect?.(campaign);
  };

  const modal = (
    <div 
      className="campaign-picker__backdrop" 
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="campaign-picker__container">
        <div className="campaign-picker__header">
          <div className="campaign-picker__icon-wrapper">
            <div className="campaign-picker__orbit" />
            <div className="campaign-picker__particle" />
            <div className="campaign-picker__particle campaign-picker__particle--secondary" />
            <div className="campaign-picker__icon">
              <span>📁</span>
            </div>
          </div>
          <h3 className="campaign-picker__title">{title}</h3>
        </div>
        
        <div className="campaign-picker__body">
          <p className="campaign-picker__message">
            {message}
          </p>
          
          <div className="campaign-picker__list">
            {campaigns.map(campaign => (
              <button
                key={campaign.id}
                className={`campaign-picker__option ${selectedCampaignId === campaign.id ? 'campaign-picker__option--selected' : ''}`}
                onClick={() => setSelectedCampaignId(campaign.id)}
                style={{ '--campaign-color': campaign.color }}
              >
                <span 
                  className="campaign-picker__option-color"
                  style={{ backgroundColor: campaign.color }}
                />
                <span className="campaign-picker__option-icon">
                  {renderIcon(campaign.icon, { size: '16px', fallback: <DefaultCampaignIcon size={16} /> })}
                </span>
                <span className="campaign-picker__option-name">{campaign.name}</span>
              </button>
            ))}
          </div>
        </div>
        
        <div className="campaign-picker__footer">
          <button 
            className="campaign-picker__btn campaign-picker__btn--cancel"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button 
            ref={confirmBtnRef}
            className="campaign-picker__btn campaign-picker__btn--confirm"
            onClick={handleConfirm}
            disabled={!selectedCampaignId}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default CampaignPickerModal;
